// Fixed AddLinkForm.tsx - Chrome Compatible Version
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { TagSelector } from "@/components/ui/tag-selector";
import { Link, Tag } from '../../types';
import useTagStore from '../../stores/tag.store';
import useCollectionStore from '../../stores/collection.store';
import { toast } from 'sonner';
import { linkService } from '../../services/api';
import { tagService } from '../../services/tags-api';
import { Loader2, Search } from 'lucide-react';
import { extractURLMetadata } from '../../utils/metadata';

interface LinkFormData {
  title: string;
  summary: string;
  url: string;
  notes: string;
  tag_ids?: number[];
  collection_id?: number | undefined;
}

interface AddLinkFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (link: Link) => void;
  editingLink?: Link | null;
  defaultCollectionId?: number;
  defaultTags?: Tag[];
}

export function AddLinkForm({ 
  isOpen, 
  onOpenChange, 
  onSuccess, 
  editingLink,
  defaultCollectionId,
  defaultTags = []
}: AddLinkFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>(defaultTags);
  const [isExtractingMetadata, setIsExtractingMetadata] = useState(false);
  const { tags, fetchTags } = useTagStore();
  const { collections } = useCollectionStore();
  
  // Add refs for better Chrome compatibility
  const metadataTimeoutRef = useRef<NodeJS.Timeout>();
  const urlProcessingRef = useRef<string>('');
  
  const [formData, setFormData] = useState<LinkFormData>({
    title: '',
    summary: '',
    url: '',
    notes: '',
    tag_ids: defaultTags.map(tag => tag.id),
    collection_id: defaultCollectionId,
  });

  // Initialize tags on mount
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Initialize form with editing data if available
  useEffect(() => {
    if (editingLink) {
      setFormData({
        title: editingLink.title || '',
        summary: editingLink.summary || '',
        url: editingLink.url || '',
        notes: editingLink.notes || '',
        tag_ids: editingLink.tag_ids || [],
        collection_id: editingLink.collection_id,
      });
      setSelectedTags(editingLink.tags || []);
    }
  }, [editingLink]);

  // Safe title generation function
  const generateSafeTitle = useCallback((url: string): string => {
    try {
      if (!url) return '';
      
      // Handle specific platforms
      if (url.includes('linkedin.com')) {
        if (url.includes('/in/')) {
          const parts = url.split('/in/')[1]?.split('/')[0]?.split('?')[0];
          return parts ? `LinkedIn: ${parts}` : 'LinkedIn Profile';
        } else if (url.includes('/company/')) {
          const parts = url.split('/company/')[1]?.split('/')[0]?.split('?')[0];
          return parts ? `${parts} | LinkedIn` : 'LinkedIn Company';
        }
        return 'LinkedIn Content';
      }
      
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        return 'YouTube Video';
      }
      
      if (url.includes('github.com')) {
        const parts = url.split('github.com/')[1]?.split('/');
        if (parts && parts.length >= 2) {
          return `${parts[0]}/${parts[1]} | GitHub`;
        }
        return 'GitHub Repository';
      }
      
      // For other URLs, extract domain
      try {
        const urlObj = new URL(url);
        const domain = urlObj.hostname.replace('www.', '');
        return `Content from ${domain}`;
      } catch {
        return 'External Link';
      }
    } catch (error) {
      console.warn('Title generation failed:', error);
      return 'External Link';
    }
  }, []);

  // Chrome-safe input change handler
  const handleInputChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    console.log(`Input change: ${name} = ${value}`); // Debug log
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If URL changed, process it after a delay (Chrome-safe)
    if (name === 'url' && value && (value.startsWith('http://') || value.startsWith('https://'))) {
      urlProcessingRef.current = value;
      
      // Clear any existing timeout
      if (metadataTimeoutRef.current) {
        clearTimeout(metadataTimeoutRef.current);
      }
      
      // Generate safe title immediately if no title exists
      if (!formData.title) {
        const safeTitle = generateSafeTitle(value);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          title: safeTitle
        }));
      }
      
      // Try to extract better metadata after delay
      metadataTimeoutRef.current = setTimeout(async () => {
        await extractMetadataForURL(value);
      }, 1000);
    }
  }, [formData.title, generateSafeTitle]);

  // Safe metadata extraction function
  const extractMetadataForURL = useCallback(async (url: string) => {
    if (!url || isExtractingMetadata || urlProcessingRef.current !== url) {
      return;
    }

    try {
      console.log('Extracting metadata for:', url); // Debug log
      setIsExtractingMetadata(true);
      
      // Set timeout for metadata extraction
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout')), 5000);
      });
      
      const metadataPromise = extractURLMetadata(url);
      const metadata = await Promise.race([metadataPromise, timeoutPromise]);
      
      // Only update if we're still on the same URL
      if (urlProcessingRef.current === url && metadata) {
        setFormData(prev => ({
          ...prev,
          title: (metadata as any).title || prev.title,
          summary: (metadata as any).description || prev.summary,
        }));
        console.log('Metadata extracted successfully'); // Debug log
      }
    } catch (error) {
      console.warn('Metadata extraction failed:', error);
      // Don't show error to user, just use the safe title we already set
    } finally {
      setIsExtractingMetadata(false);
    }
  }, [isExtractingMetadata]);

  // Manual metadata refresh button
  const handleRefreshMetadata = useCallback(async () => {
    if (!formData.url) {
      toast.error('Please enter a URL first');
      return;
    }

    try {
      setIsExtractingMetadata(true);
      const metadata = await extractURLMetadata(formData.url);
      
      if (metadata) {
        setFormData(prev => ({
          ...prev,
          title: metadata.title || prev.title,
          summary: metadata.description || prev.summary,
        }));
        toast.success('Metadata extracted successfully');
      } else {
        // Generate safe title as fallback
        const safeTitle = generateSafeTitle(formData.url);
        setFormData(prev => ({
          ...prev,
          title: safeTitle
        }));
        toast.info('Generated title from URL');
      }
    } catch (error) {
      console.warn('Manual metadata extraction failed:', error);
      // Generate safe title as fallback
      const safeTitle = generateSafeTitle(formData.url);
      setFormData(prev => ({
        ...prev,
        title: safeTitle
      }));
      toast.info('Generated title from URL');
    } finally {
      setIsExtractingMetadata(false);
    }
  }, [formData.url, generateSafeTitle]);

  const handleCollectionSelect = (value: string) => {
    const collectionId = value === "none" ? undefined : parseInt(value);
    setFormData(prev => ({
      ...prev,
      collection_id: collectionId
    }));
  };

  const handleTagSelect = (tag: Tag) => {
    if (selectedTags.length >= 5) {
      toast.error('Maximum 5 tags allowed per link');
      return;
    }
    
    if (!selectedTags.some(t => t.id === tag.id)) {
      const newSelectedTags = [...selectedTags, tag];
      setSelectedTags(newSelectedTags);
      setFormData(prev => ({
        ...prev,
        tag_ids: newSelectedTags.map(t => t.id)
      }));
    }
  };

  const handleTagRemove = (tagId: number) => {
    const newSelectedTags = selectedTags.filter(tag => tag.id !== tagId);
    setSelectedTags(newSelectedTags);
    setFormData(prev => ({
      ...prev,
      tag_ids: newSelectedTags.map(t => t.id)
    }));
  };

  const handleTagCreate = async (name: string) => {
    try {
      const newTag = await tagService.createTag({ name });
      await fetchTags();
      handleTagSelect(newTag);
      toast.success(`Tag "${name}" created and added`);
    } catch (error) {
      console.error('Error creating tag:', error);
      toast.error('Failed to create tag');
      throw error;
    }
  };

  // REMOVED: Complex onPaste handler that was causing Chrome issues
  // REMOVED: onBlur handler that was interfering with form state

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);
      
      // Prepare the data WITHOUT tag_ids for creation
      const submitData = {
        url: formData.url,
        title: formData.title,
        summary: formData.summary || '',
        notes: formData.notes || '',
        collection_id: formData.collection_id,
      };

      let response;
      if (editingLink) {
        // For updates, include tag_ids
        const updateData = {
          ...submitData,
          tag_ids: formData.tag_ids?.length ? formData.tag_ids : undefined
        };
        response = await linkService.updateLink(editingLink.id, updateData);
        toast.success('Link updated successfully');
      } else {
        // Create new link first WITHOUT tags
        response = await linkService.createLink(submitData);
        
        // If we have tags to add, add them after creation
        if (selectedTags.length > 0) {
          try {
            const tagIds = selectedTags.map(tag => tag.id);
            const updatedLink = await linkService.updateLink(response.id, {
              tag_ids: tagIds
            });
            
            // Create the final link object with both tag_ids and tags
            response = {
              ...updatedLink,
              tag_ids: tagIds,
              tags: selectedTags
            };
            toast.success('Link created with tags successfully');
          } catch (tagError) {
            console.error('Failed to add tags:', tagError);
            toast.warning('Link created but failed to add tags');
          }
        } else {
          toast.success('Link created successfully');
        }
      }
      
      // Call onSuccess with the final link object
      onSuccess?.(response);
      
      // Reset form and close dialog
      onOpenChange(false);
      setFormData({
        title: '',
        summary: '',
        url: '',
        notes: '',
        tag_ids: [],
        collection_id: defaultCollectionId,
      });
      setSelectedTags([]);
    } catch (error) {
      console.error('Failed to save link:', error);
      toast.error(editingLink ? 'Failed to update link' : 'Failed to create link');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (metadataTimeoutRef.current) {
        clearTimeout(metadataTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        console.log('Dialog open change:', open); // Debug log
        onOpenChange(open);
        if (!open) {
          // Cleanup timeouts
          if (metadataTimeoutRef.current) {
            clearTimeout(metadataTimeoutRef.current);
          }
          
          // Reset form state when dialog closes
          setFormData({
            title: '',
            summary: '',
            url: '',
            notes: '',
            tag_ids: [],
            collection_id: defaultCollectionId,
          });
          setSelectedTags([]);
          setIsSubmitting(false);
          setIsExtractingMetadata(false);
          urlProcessingRef.current = '';
        }
      }}
    >
      <DialogContent 
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className="sm:max-w-[425px]"
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{editingLink ? 'Edit Link' : 'Add Link'}</DialogTitle>
            <DialogDescription>
              {editingLink ? 'Edit link information.' : 'Add link information.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* CHROME-SAFE URL INPUT */}
            <div className="grid gap-2">
              <Label htmlFor="url">URL</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="url"
                    name="url"
                    type="url"
                    value={formData.url || ''}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    required
                    disabled={isExtractingMetadata}
                    className={isExtractingMetadata ? "pr-10" : ""}
                  />
                  {isExtractingMetadata && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshMetadata}
                  disabled={!formData.url || isExtractingMetadata}
                  title="Extract metadata from URL"
                >
                  {isExtractingMetadata ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Enter URL and click the search button to extract metadata
              </p>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                value={formData.title || ''}
                onChange={handleInputChange}
                placeholder="Link title"
                required
              />
            </div>
            
            {/* Collection Selection */}
            <div className="grid gap-2">
              <Label htmlFor="collection">Collection</Label>
              <select
                id="collection"
                value={formData.collection_id ? formData.collection_id.toString() : "none"}
                onChange={(e) => handleCollectionSelect(e.target.value)}
                disabled={isSubmitting || collections.length === 0}
                className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="none">No collection</option>
                {collections.map((collection) => (
                  <option key={collection.id} value={collection.id.toString()}>
                    {collection.name}
                  </option>
                ))}
              </select>
            </div>
            
            <Tabs defaultValue="summary" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="summary">Summary</TabsTrigger>
                <TabsTrigger value="notes">Notes</TabsTrigger>
              </TabsList>
              <TabsContent value="summary">
                <div className="grid gap-2">
                  <Label htmlFor="summary">Summary</Label>
                  <Textarea
                    id="summary"
                    name="summary"
                    value={formData.summary || ''}
                    onChange={handleInputChange}
                    placeholder="Link summary"
                    className="min-h-[100px] resize-none"
                  />
                </div>
              </TabsContent>
              <TabsContent value="notes">
                <div className="grid gap-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    name="notes"
                    value={formData.notes || ''}
                    onChange={handleInputChange}
                    placeholder="Add your notes here"
                    className="min-h-[100px] resize-none"
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* Tags Section */}
            <div className="grid gap-2">
              <Label>Tags</Label>
              <TagSelector
                tags={tags}
                selectedTags={selectedTags}
                onTagSelect={handleTagSelect}
                onTagRemove={handleTagRemove}
                onTagCreate={handleTagCreate}
                disabled={isSubmitting}
                variant="default"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}